<template>
  <UiModal v-model="isNoticeModalOpen" :title="t('common.noticeBoard.title')" size="large">
    <div class="notice-board-modal">
      <div class="notice-board-search flex align-center gap-10 flex-wrap">
        <div class="flex align-center gap-5">
          <label for="noticeColName">{{ t('common.noticeBoard.searchCondition') }}</label>
          <UiSelect id="noticeColName" v-model="noticeFind.colName" :options="colNameOptions" width="100px" />
        </div>
        <div class="flex align-center gap-5">
          <UiInput
            id="noticeSearchKeyword"
            v-model="noticeFind.searchKeyword"
            class="notice-board-search-input"
            maxlength="20"
            :placeholder="t('common.noticeBoard.searchKeyword')"
            @keyup.enter="handleNoticeSearch"
          />
        </div>
        <UiButton variant="primary" @click="handleNoticeSearch">{{ t('common.action.search') }}</UiButton>
      </div>

      <UiTableDefault v-model="noticeList" :column-count="3" class="notice-board-table" hover striped>
        <template #header>
          <tr>
            <th class="txt-c" style="width: 80px">{{ t('common.noticeBoard.number') }}</th>
            <th>{{ t('common.noticeBoard.subject') }}</th>
            <th class="txt-c" style="width: 150px">{{ t('common.noticeBoard.registeredDate') }}</th>
          </tr>
        </template>
        <template #body="{ rows }">
          <tr v-for="item in rows" :key="item.id">
            <td class="txt-c">{{ item.id }}</td>
            <td>
              <a href="#none" class="notice-board-title-link" @click.prevent="handleNoticeDetail(item)">
                {{ item.subject }}
              </a>
            </td>
            <td class="txt-c">{{ item.createDtStr }}</td>
          </tr>
        </template>
        <template #empty>
          <tr>
            <td colspan="3" class="txt-c">{{ t('common.noticeBoard.empty') }}</td>
          </tr>
        </template>
      </UiTableDefault>

      <div v-if="isNoticeDetailVisible && noticeDetail" class="notice-board-detail">
        <div class="notice-board-detail__title">
          {{ t('common.noticeBoard.detailTitle', { subject: noticeDetail.subject }) }}
        </div>
        <table class="notice-board-detail__table">
          <colgroup>
            <col style="width: 20%" />
            <col style="width: 80%" />
          </colgroup>
          <tbody>
            <tr>
              <th>{{ t('common.noticeBoard.subject') }}</th>
              <td>{{ noticeDetail.subject }}</td>
            </tr>
            <tr>
              <th>{{ t('common.noticeBoard.noticeContent') }}</th>
              <td class="notice-board-detail__content">{{ noticeDetail.content }}</td>
            </tr>
            <tr>
              <th>{{ t('common.attachments') }}</th>
              <td>
                <UiFileAttachment v-model="noticeAttachmentFiles" :isFileUploadDisabled="true" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </UiModal>
</template>

<script setup>
import { computed } from 'vue';
import UiFileAttachment from '~/components/attachment/UiFileAttachment.vue';
import UiSelect from '~/components/common/UI/UiSelect.vue';
import { useNoticeBoardStore } from '~/composables/user/useNoticeBoardStore';

const { t } = useI18n();

const {
  isNoticeModalOpen,
  isNoticeDetailVisible,
  noticeList,
  noticeDetail,
  noticeAttachmentFiles,
  noticeFind,
  handleNoticeSearch,
  handleNoticeDetail,
} = useNoticeBoardStore();

const colNameOptions = computed(() => [
  { label: t('common.noticeBoard.subject'), value: 'subject' },
  { label: t('common.noticeBoard.content'), value: 'content' },
]);
</script>

<style lang="scss" scoped>
.notice-board-modal {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.notice-board-search {
  padding-top: 4px;

  label {
    white-space: nowrap;
    font-size: $font-size-sm;
  }
}

.notice-board-search-input {
  width: 300px;
}

.notice-board-title-link {
  color: $text-color;
  text-decoration: none;

  &:hover {
    color: $primary-color;
    text-decoration: underline;
  }
}

.notice-board-detail {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 8px;
  border-top: 1px solid $border-color;

  &__title {
    font-weight: 600;
  }

  &__table {
    width: 100%;
    border-collapse: collapse;
    font-size: $font-size-sm;

    th,
    td {
      border: 1px solid $border-color;
      padding: 10px 12px;
      vertical-align: top;
    }

    th {
      background: #f8f9fa;
      text-align: left;
    }
  }

  &__content {
    white-space: pre-wrap;
    word-break: break-word;
  }
}
</style>
